import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  normalizeSaccosResponse,
  saccoOptionsToLookupMap,
  type SaccoOption,
} from "../components/SaccoSelect";
import { fetchSaccos } from "../services/apiCalls";
import type { AppSessionUser } from "../types/session";

export type SaccoListContextValue = {
  saccoOptions: SaccoOption[];
  saccoNameById: Map<string, string>;
  loading: boolean;
};

const emptyMap = () => new Map<string, string>();

const defaultValue: SaccoListContextValue = {
  saccoOptions: [],
  saccoNameById: emptyMap(),
  loading: false,
};

export const SaccoListContext = createContext<SaccoListContextValue>(defaultValue);

export function useSaccoList(): SaccoListContextValue {
  return useContext(SaccoListContext);
}

type SaccoListProviderProps = {
  children: React.ReactNode;
  user: AppSessionUser | undefined;
};

/**
 * Loads GET /sacco once per signed-in session and shares options + id→name map app-wide.
 */
export function SaccoListProvider({ children, user }: SaccoListProviderProps) {
  const [saccoOptions, setSaccoOptions] = useState<SaccoOption[]>([]);
  const [loading, setLoading] = useState(false);

  const shouldFetch = Boolean(
    user?.authResolved && (user.saccoUser != null || user.currentUser != null)
  );

  useEffect(() => {
    if (!shouldFetch) {
      setSaccoOptions([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchSaccos()
      .then((raw) => {
        if (!cancelled) setSaccoOptions(normalizeSaccosResponse(raw));
      })
      .catch(() => {
        if (!cancelled) setSaccoOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [shouldFetch]);

  const saccoNameById = useMemo(
    () => saccoOptionsToLookupMap(saccoOptions),
    [saccoOptions]
  );

  const value = useMemo<SaccoListContextValue>(
    () => ({
      saccoOptions,
      saccoNameById,
      loading,
    }),
    [saccoOptions, saccoNameById, loading]
  );

  return (
    <SaccoListContext.Provider value={value}>{children}</SaccoListContext.Provider>
  );
}
